import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ArrowRight, CalendarDays } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { upcomingEvents } from "@/lib/events";

const EASE = [0.22, 1, 0.36, 1];

function KineticHeadline({ text }) {
  const lines = text.split("\n").filter(Boolean);
  return (
    <h1
      data-testid="hero-headline"
      className="font-heading font-bold uppercase text-white text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.95]"
    >
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-1">
          <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 0.55 + i * 0.14, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function Home() {
  const { content } = useSite();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const c = content.contact;
  const homeEvents = upcomingEvents(content.events).slice(0, Number(content.home_events_count) || 3);

  const contactCards = [
    { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}`, testid: "preview-email" },
    { icon: Phone, label: "Main number", value: c.phone, href: `tel:${c.phone.replace(/\D/g, "")}`, testid: "preview-phone" },
    { icon: MapPin, label: "Address", value: c.address, href: "/contact", testid: "preview-address" },
    { icon: Clock, label: "Office hours", value: c.hours, href: "/contact", testid: "preview-hours" },
  ];

  return (
    <main data-testid="home-page">
      <section className="relative min-h-screen flex items-center overflow-hidden bg-navy grain" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          {content.loaded && content.hero_video ? (
            <motion.video
              key={content.hero_video}
              src={content.hero_video}
              autoPlay
              muted
              loop
              playsInline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="w-full h-full object-cover"
              data-testid="hero-background-video"
            />
          ) : content.loaded && content.hero_image ? (
            <motion.img
              key={content.hero_image}
              src={content.hero_image}
              alt="Drywall and construction work in progress"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="w-full h-full object-cover"
              data-testid="hero-background-image"
            />
          ) : null}
        </motion.div>
        <div className="absolute inset-0 bg-navy/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 w-full text-center">
          <motion.img
            src={content.logo_url}
            alt="Drywall Local 675 emblem"
            data-testid="hero-logo"
            initial={{ opacity: 0, scale: 0.55, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 90, damping: 14, delay: 0.15 }}
            className={`h-32 sm:h-40 lg:h-48 w-auto mx-auto mb-10 ${content.logo_backdrop === "white" ? "bg-white rounded-sm px-4 py-3 shadow-2xl" : "drop-shadow-[0_4px_20px_rgba(0,0,0,0.55)]"}`}
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="text-[11px] sm:text-xs tracking-[0.35em] uppercase font-bold text-brand-sky mb-8"
            data-testid="hero-eyebrow"
          >
            Drywall · Acoustic · Lathing · Insulation
          </motion.p>
          <KineticHeadline text={content.hero_headline} />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-8 text-base sm:text-lg text-white/80 max-w-xl mx-auto leading-relaxed"
            data-testid="hero-subhead"
          >
            {content.hero_subhead}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/events"
              data-testid="hero-events-button"
              className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300"
            >
              View upcoming events
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white/10 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300"
            >
              Contact the office
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="w-px h-12 bg-gradient-to-b from-transparent via-white/60 to-white/10"
          />
        </motion.div>
      </section>

      <section className="py-24 sm:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <ChapterLabel number="01" label="Who we are" testid="about-chapter" />
            <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-snug text-slate-900 font-bold" data-testid="about-text">
              {content.about_text}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-brand" />
              <img
                src="https://images.pexels.com/photos/11427055/pexels-photo-11427055.jpeg"
                alt="Skilled tradesperson installing drywall"
                className="relative w-full aspect-[4/3] object-cover"
                data-testid="about-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-slate-50" data-testid="contact-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <ChapterLabel number="02" label="Reach the office" testid="contact-chapter" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {contactCards.map((card, i) => (
              <Reveal key={card.label} delay={i * 0.08}>
                <Link
                  to={card.href.startsWith("/") ? card.href : undefined}
                  href={card.href.startsWith("/") ? undefined : card.href}
                  onClick={(e) => { if (!card.href.startsWith("/")) { e.preventDefault(); window.location.href = card.href; } }}
                  data-testid={card.testid}
                  className="group block bg-white hover:bg-brand p-8 h-full transition-colors duration-300"
                >
                  <card.icon className="h-6 w-6 text-brand group-hover:text-white mb-6 transition-colors duration-300" />
                  <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-slate-500 group-hover:text-white/70 mb-2 transition-colors duration-300">
                    {card.label}
                  </p>
                  <p className="text-base font-semibold text-slate-900 group-hover:text-white transition-colors duration-300 break-words">
                    {card.value}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link
              to="/contact"
              data-testid="preview-full-contact-link"
              className="inline-flex items-center gap-2 mt-10 text-xs font-bold tracking-[0.2em] uppercase text-brand hover:text-brand-dark transition-colors duration-300"
            >
              Full contact details & staff directory
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="events-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <ChapterLabel number="03" label="Mark the calendar" testid="events-chapter" />
          </Reveal>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {homeEvents.length === 0 && (
              <p className="py-7 text-sm text-slate-500" data-testid="preview-events-empty">
                No upcoming events right now — check back soon.
              </p>
            )}
            {homeEvents.map((event, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-10 py-7" data-testid={`preview-event-${i}`}>
                  <div className="flex items-center gap-3 sm:w-72 shrink-0">
                    <CalendarDays className="h-4 w-4 text-brand shrink-0" />
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">{event.date}</p>
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl uppercase text-slate-900 group-hover:text-brand transition-colors duration-300 flex-1">
                    {event.title}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {[event.time, event.location].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <Link
              to="/events"
              data-testid="view-all-events-button"
              className="group inline-flex items-center gap-2 mt-10 bg-navy hover:bg-brand text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300"
            >
              View all events
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
